/* eslint-disable @typescript-eslint/no-explicit-any */
import { ComponentPropsWithRef, ComponentType } from "react";
import { Base } from "./base";
import { HOC } from "./types";

/**
 * A builder class for composing React components using HOCs.
 * Wraps the Base class and provides a fluent API for chaining HOCs.
 */
export class ComponentBuilderBlank<TAccumulatedProps = object> {
  private baseBuilder: Base<TAccumulatedProps>;

  /**
   * Initializes the builder with an optional list of HOCs.
   * @param hocs - Initial array of HOCs to apply.
   */
  constructor(hocs: HOC<any>[] = []) {
    this.baseBuilder = new Base(ComponentBuilderBlank.name);
    this.baseBuilder.setHocs = hocs;
  }

  /**
   * Adds a new HOC to the builder and returns a new instance with updated accumulated props.
   * @param hoc - A higher-order component to apply.
   * @returns A new builder instance with extended prop types.
   */
  compose<TNewProps>(hoc: HOC<TNewProps>): ComponentBuilderBlank<TAccumulatedProps & TNewProps> {
    return new ComponentBuilderBlank<TAccumulatedProps & TNewProps>([...this.baseBuilder.getHocs, hoc]);
  }

  /**
   * Adds a parameterized HOC to the builder using a factory function and its arguments.
   * @param hocWithArgs - A function that returns an HOC when called with a component and arguments.
   * @param args - Arguments to pass to the HOC factory.
   * @returns A new builder instance with extended prop types.
   */
  composeWithArgs<
    P,
    H extends <C extends ComponentType<any>>(component: C, a: P) => ComponentType<any>,
  >(hocWithArgs: H, args: P) {
    const hoc = this.baseBuilder.composeWithArgsFactory(hocWithArgs, args);
    return this.compose(hoc);
  }

  /**
   * Builds the final enhanced component by applying all composed HOCs.
   * @param Component - The base React component to enhance.
   * @returns A new component with merged props.
   */
  build<C extends ComponentType<any>>(Component: C) {
    return this.baseBuilder.build(Component);
  }

  /**
   * Builds the final enhanced component with some props pre-injected.
   * @param Component - The base React component to enhance.
   * @param injectedProps - A subset of props to inject automatically.
   * @returns A component with the remaining props required.
   */
  buildWithProps<C extends ComponentType<any>, K extends keyof (ComponentPropsWithRef<C> & TAccumulatedProps)>(
    Component: C,
    injectedProps: Pick<ComponentPropsWithRef<C> & TAccumulatedProps, K>,
  ) {
    return this.baseBuilder.buildWithProps(Component, injectedProps);
  }
}
